/**
 * Immunization Schedule API
 * API functions for managing future immunization doses
 */

import { API_ENDPOINTS } from '../config/api';
import {
    ImmunizationSchedule,
    PetSchedule,
    CreateScheduleRequest,
    RescheduleRequest,
    CancelScheduleRequest
} from '../types/immunization-schedule';

/**
 * Get immunization schedule for a pet
 */
export async function getPetSchedule(
    petId: string,
    includeCompleted: boolean = false
): Promise<PetSchedule> {
    const params = new URLSearchParams();
    if (includeCompleted) {
        params.append('include_completed', 'true');
    }

    const url = `${API_ENDPOINTS.PETS.SCHEDULE(petId)}?${params.toString()}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch schedule: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Create a manual schedule entry
 */
export async function createScheduleEntry(
    petId: string,
    data: CreateScheduleRequest
): Promise<ImmunizationSchedule> {
    const response = await fetch(API_ENDPOINTS.PETS.SCHEDULE(petId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        throw new Error(`Failed to create schedule entry: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Reschedule a dose
 */
export async function rescheduleScheduleEntry(
    scheduleId: string,
    data: RescheduleRequest
): Promise<ImmunizationSchedule> {
    const response = await fetch(API_ENDPOINTS.SCHEDULE.RESCHEDULE(scheduleId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        throw new Error(`Failed to reschedule: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Cancel a scheduled dose
 */
export async function cancelScheduleEntry(
    scheduleId: string,
    data: CancelScheduleRequest
): Promise<ImmunizationSchedule> {
    const response = await fetch(API_ENDPOINTS.SCHEDULE.CANCEL(scheduleId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        throw new Error(`Failed to cancel: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Delete a schedule entry (hard delete)
 */
export async function deleteScheduleEntry(scheduleId: string): Promise<void> {
    const response = await fetch(API_ENDPOINTS.SCHEDULE.DELETE(scheduleId), {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to delete: ${response.statusText}`);
    }
}

/**
 * Get overdue immunizations for a practice
 */

export async function getPracticeOverdue(practiceId: string): Promise<ImmunizationSchedule[]> {
    const response = await fetch(API_ENDPOINTS.SCHEDULE.PRACTICE_OVERDUE(practiceId), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch overdue: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get available immunization templates
 */
export async function getImmunizationTemplates(species?: string): Promise<import('../types/immunization-schedule').ImmunizationTemplate[]> {
    const url = new URL(API_ENDPOINTS.SCHEDULE.TEMPLATES);
    if (species) {
        url.searchParams.append('species', species);
    }

    const response = await fetch(url.toString(), {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
    });

    if (!response.ok) {
        throw new Error('Failed to fetch immunization templates');
    }

    return response.json();
}

/**
 * Apply an immunization template to a pet
 */
export async function applyImmunizationTemplate(
    petId: string,
    data: import('../types/immunization-schedule').ApplyTemplateRequest
): Promise<ImmunizationSchedule[]> {
    const response = await fetch(API_ENDPOINTS.SCHEDULE.APPLY_TEMPLATE(petId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error('Failed to apply immunization template');
    }

    return response.json();
}
